import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Download, X, ExternalLink, Zap, FileText,
  Loader2, Check, AlertCircle,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { bulkCreatePSPixels } from "@/lib/api";

const AXIOM_QUERY_URL = "https://axiom.veracross.com/#/results/1462309";

// Minimal CSV parser — handles quoted fields, embedded commas, CRLF, doubled quotes.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const src = (text || "").replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else {
        field += c;
      }
    } else {
      if (c === '"') { inQuotes = true; }
      else if (c === ",") { row.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && src[i + 1] === "\n") i++;
        row.push(field); field = "";
        if (row.length > 1 || row[0] !== "") rows.push(row);
        row = [];
      } else { field += c; }
    }
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

const normalizeHeader = (h) => (h || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");

const REQUIRED = [
  { key: "portal_screen_id", label: "Portal Screen ID" },
  { key: "portal", label: "Portal" },
  { key: "description", label: "Description" },
  { key: "access_name", label: "Access Name" },
];

export default function PSPixelBulk() {
  const nav = useNavigate();
  const [csvText, setCsvText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [archiveMissing, setArchiveMissing] = useState(false);
  const fileInputRef = useRef(null);

  const parsed = useMemo(() => {
    const text = csvText.trim();
    if (!text) return { ok: false, rows: [], error: null };
    const grid = parseCsv(text);
    if (grid.length < 2) return { ok: false, rows: [], error: "Need a header row + at least one data row." };
    const headers = grid[0].map(normalizeHeader);
    const idx = {};
    for (const col of REQUIRED) idx[col.key] = headers.indexOf(col.key);
    const missing = REQUIRED.filter((c) => idx[c.key] === -1).map((c) => c.label);
    if (missing.length) {
      return { ok: false, rows: [],
        error: `Missing required column${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}. CSV must include: Portal Screen ID, Portal, Description, Access Name.` };
    }
    const rows = grid.slice(1)
      .filter((r) => r.some((c) => (c || "").trim() !== ""))
      .map((r) => ({
        portal_screen_id: (r[idx.portal_screen_id] || "").trim(),
        portal: (r[idx.portal] || "").trim(),
        description: (r[idx.description] || "").trim(),
        access_name: (r[idx.access_name] || "").trim(),
      }))
      .filter((r) => r.portal_screen_id);
    const seen = new Set();
    const deduped = [];
    for (const r of rows) {
      if (seen.has(r.portal_screen_id)) continue;
      seen.add(r.portal_screen_id);
      deduped.push(r);
    }
    return { ok: true, rows: deduped, error: null };
  }, [csvText]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setCsvText(text);
      toast.success(`Loaded ${file.name}`);
    } catch {
      toast.error("Could not read file");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clear = () => { setCsvText(""); setResult(null); };

  const onSubmit = async () => {
    if (!parsed.ok || !parsed.rows.length) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await bulkCreatePSPixels({ rows: parsed.rows, archive_missing: archiveMissing });
      setResult(res);
      const arch = res.archived ? ` · ${res.archived} archived` : "";
      toast.success(`${res.created} created · ${res.updated} updated${arch}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Bulk import failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadResultCsv = () => {
    if (!result?.rows?.length) return;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Portal Screen ID", "Portal", "Description", "Access Name", "Status", "Pixel URL"];
    const lines = [header.map(esc).join(",")];
    for (const r of result.rows) {
      lines.push([r.portal_screen_id, r.portal, r.description, r.access_name, r.status, r.embed_url].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "portalstudio-pixel-urls.csv";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-4"
              data-testid="bulk-back-link">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>

        <div className="mb-8">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">
            PortalStudio Pixel · Bulk import
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
            Create one tracking pixel per portal page
          </h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-2xl leading-relaxed">
            Upload the screens list exported from Veracross Axiom. Each row becomes its own
            pixel with a stable tracking URL. Re-uploading later updates the portal name,
            description or access name — the tracking URL never changes (keyed by <strong>Portal Screen ID</strong>).
          </p>
        </div>

        <div className="border border-amber-200 bg-amber-50/60 rounded-md p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-amber-700" />
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-amber-800">
              How to get the CSV from Veracross
            </div>
          </div>
          <ol className="text-[13px] text-zinc-700 space-y-1.5 leading-relaxed list-decimal pl-5">
            <li>
              Open the Axiom query:{" "}
              <a href={AXIOM_QUERY_URL} target="_blank" rel="noopener noreferrer"
                 className="font-mono text-[12px] text-amber-800 hover:text-amber-900 underline decoration-amber-300 underline-offset-2 inline-flex items-center gap-1"
                 data-testid="bulk-axiom-link">
                axiom.veracross.com/#/results/1462309
                <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>Click <strong>Run</strong>, then the <strong>lightning bolt</strong> <Zap className="inline h-3 w-3 text-amber-700" /> icon, and choose <strong>Export to CSV</strong>.</li>
            <li>Upload the file below (or paste the contents). Required columns: <span className="font-mono text-[11px] bg-white/70 px-1 rounded">Portal Screen ID</span>, <span className="font-mono text-[11px] bg-white/70 px-1 rounded">Portal</span>, <span className="font-mono text-[11px] bg-white/70 px-1 rounded">Description</span>, <span className="font-mono text-[11px] bg-white/70 px-1 rounded">Access Name</span>.</li>
          </ol>
        </div>

        <div className="border border-zinc-200 bg-white rounded-md p-5 mb-6 flex items-start gap-3"
             data-testid="archive-missing-card">
          <Switch
            id="archive-missing"
            checked={archiveMissing}
            onCheckedChange={setArchiveMissing}
            data-testid="archive-missing-switch"
            className="mt-0.5"
          />
          <div className="flex-1">
            <Label htmlFor="archive-missing"
                   className="text-[13px] font-semibold text-zinc-950 cursor-pointer">
              Archive pixels missing from this upload
            </Label>
            <p className="text-[12px] text-zinc-500 mt-1 leading-relaxed">
              When on, any existing pixel whose <span className="font-mono">Portal Screen ID</span> is
              not in this CSV will be marked as archived — hidden from dashboards but analytics history
              is preserved. Re-uploading a Portal Screen ID later automatically un-archives it.
              Manually-created pixels (no Portal Screen ID) are never touched.
            </p>
          </div>
        </div>

        <div className="border border-zinc-200 bg-white rounded-md p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">
              Paste or upload CSV
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="h-9 text-xs"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="bulk-upload-file-btn">
                <Upload className="h-3.5 w-3.5 mr-2" /> Upload CSV
              </Button>
              <input ref={fileInputRef} type="file" accept=".csv,text/csv"
                     onChange={onFile} className="hidden"
                     data-testid="bulk-file-input" />
              {csvText ? (
                <Button type="button" variant="outline" className="h-9 text-xs"
                        onClick={clear} data-testid="bulk-clear-btn">
                  <X className="h-3.5 w-3.5 mr-2" /> Clear
                </Button>
              ) : null}
            </div>
          </div>
          <Textarea
            value={csvText}
            onChange={(e) => { setCsvText(e.target.value); setResult(null); }}
            placeholder={"Portal Screen ID,Portal,Description,Access Name\n1554,Parent,Learning Support,learning-support\n1683,Parent,Enrollment Forms 2026,forms/enrollment-2026"}
            className="font-mono text-[12px] min-h-[160px]"
            data-testid="bulk-csv-textarea"
          />
          {parsed.error ? (
            <div className="mt-3 text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2"
                 data-testid="bulk-csv-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{parsed.error}</span>
            </div>
          ) : null}
        </div>

        {parsed.ok && parsed.rows.length > 0 && !result ? (
          <div className="border border-zinc-200 bg-white rounded-md overflow-hidden mb-6"
               data-testid="bulk-preview">
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 flex items-center gap-2">
                <FileText className="h-3 w-3" />
                {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} parsed
              </div>
              <Button type="button" onClick={onSubmit} disabled={submitting}
                      className="h-9 text-xs bg-zinc-950 text-white hover:bg-zinc-800"
                      data-testid="bulk-submit-btn">
                {submitting ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-2" />}
                Create / update {parsed.rows.length} pixel{parsed.rows.length === 1 ? "" : "s"}
              </Button>
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-zinc-50 sticky top-0 z-10">
                  <tr className="text-left text-zinc-500">
                    <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Portal Screen ID</th>
                    <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Portal</th>
                    <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Description</th>
                    <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Access Name</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {parsed.rows.map((r, i) => (
                    <tr key={r.portal_screen_id + i} className="hover:bg-zinc-50/60">
                      <td className="px-3 py-1.5 font-mono text-zinc-700">{r.portal_screen_id}</td>
                      <td className="px-3 py-1.5 text-zinc-800">{r.portal || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-1.5 text-zinc-800 max-w-[260px] truncate" title={r.description}>{r.description || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-3 py-1.5 font-mono text-zinc-700">{r.access_name || <span className="text-zinc-400">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {result ? (
          <div className="border border-green-200 bg-green-50/60 rounded-md p-6 mb-6"
               data-testid="bulk-result">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-md grid place-items-center bg-green-100 text-green-700 border border-green-200 shrink-0">
                <Check className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-green-800">Done</div>
                <h2 className="font-display text-xl font-semibold text-zinc-950 mt-1">
                  {result.total} pixel{result.total === 1 ? "" : "s"} ready
                </h2>
                <p className="text-[13px] text-zinc-700 mt-1">
                  <strong>{result.created}</strong> created ·{" "}
                  <strong>{result.updated}</strong> updated (existing tracking URLs preserved)
                  {result.unarchived ? <> · <strong>{result.unarchived}</strong> restored from archive</> : null}
                  {result.archived ? <> · <strong>{result.archived}</strong> archived (missing from upload)</> : null}
                </p>
                <div className="flex gap-2 mt-4">
                  <Button type="button" onClick={downloadResultCsv}
                          className="h-9 text-xs bg-zinc-950 text-white hover:bg-zinc-800"
                          data-testid="bulk-download-result-btn">
                    <Download className="h-3.5 w-3.5 mr-2" /> Download pixel-URL mapping
                  </Button>
                  <Button type="button" variant="outline" className="h-9 text-xs"
                          onClick={() => nav("/dashboard")}
                          data-testid="bulk-go-dashboard-btn">
                    Back to dashboard
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-6 border border-zinc-200 bg-white rounded-md overflow-hidden">
              <div className="max-h-[320px] overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-zinc-50 sticky top-0 z-10">
                    <tr className="text-left text-zinc-500">
                      <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Portal Screen ID</th>
                      <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Portal</th>
                      <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Description</th>
                      <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Status</th>
                      <th className="px-3 py-2 font-semibold tracking-wide uppercase text-[10px]">Pixel URL</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {result.rows.map((r) => (
                      <tr key={r.portal_screen_id} className="hover:bg-zinc-50/60">
                        <td className="px-3 py-1.5 font-mono text-zinc-700">{r.portal_screen_id}</td>
                        <td className="px-3 py-1.5 text-zinc-800">{r.portal || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-3 py-1.5 text-zinc-800 max-w-[220px] truncate" title={r.description}>{r.description || <span className="text-zinc-400">—</span>}</td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                            r.status === "created" ? "bg-blue-100 text-blue-900"
                                                   : "bg-zinc-100 text-zinc-600"
                          }`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-600 max-w-[320px] truncate"
                            title={r.embed_url}>{r.embed_url}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
